/**
 * Utility functions to transform object keys between snake_case and camelCase
 */

/**
 * Converts snake_case string to camelCase
 * Handles both letters and digits after underscores (e.g., address_1 -> address1)
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z0-9])/gi, (match, char) => char.toUpperCase());
};

/**
 * Converts camelCase string to snake_case
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * Recursively transforms object keys from snake_case to camelCase
 */
export const transformSnakeToCamel = <T = unknown>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformSnakeToCamel) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      transformed[camelKey] = transformSnakeToCamel(value);
    }
    return transformed as T;
  }

  return obj;
};

/**
 * Recursively transforms object keys from camelCase to snake_case
 */
export const transformCamelToSnake = <T = unknown>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformCamelToSnake) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const snakeKey = camelToSnake(key);
      transformed[snakeKey] = transformCamelToSnake(value);
    }
    return transformed as T;
  }

  return obj;
};

/**
 * Transform query parameters from camelCase to snake_case
 */
export const transformQueryParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const transformed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    transformed[camelToSnake(key)] = value;
  }
  return transformed;
};